import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit — a straight wall segment with a hinged oak door in the kit's
// standard 0.9 x 2.05 m opening. Two variants: "closed" (default) and "open"
// (the leaf swung about 80 degrees into +Z on its hinge edge at -X).
const PLASTER = "#e8e4dc";
const BASEBOARD = "#c9c2b6";
const OAK = "#c8975a";
const BLACK = "#2e2e30";

// Hinge edge of the leaf (model-local X of the -X jamb side of the opening).
const HINGE_X = -0.44;

/** The wall shell around the opening (shared by both variants). */
function shell(): Part[] {
  return [
    // header above the opening (2.05 -> 2.6)
    { geometry: new THREE.BoxGeometry(1.0, 0.55, 0.12), color: PLASTER, position: [0, 2.325, 0] },
    // narrow jambs at both sides of the 0.9 m opening
    { geometry: new THREE.BoxGeometry(0.05, 2.05, 0.12), color: PLASTER, position: [-0.475, 1.025, 0] },
    { geometry: new THREE.BoxGeometry(0.05, 2.05, 0.12), color: PLASTER, position: [0.475, 1.025, 0] },
    // baseboard stubs on the jambs
    { geometry: new THREE.BoxGeometry(0.05, 0.1, 0.15), color: BASEBOARD, position: [-0.475, 0.05, 0] },
    { geometry: new THREE.BoxGeometry(0.05, 0.1, 0.15), color: BASEBOARD, position: [0.475, 0.05, 0] },
  ];
}

/** The oak leaf + handle, swung `angle` radians about the hinge edge into +Z. */
function leaf(angle: number): Part[] {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // Rotate a closed-position offset (dx, dz) from the hinge into the open pose.
  const swing = (dx: number, dz: number): [number, number] => [
    HINGE_X + dx * cos - dz * sin,
    dx * sin + dz * cos,
  ];
  const [leafX, leafZ] = swing(0.44, 0);
  const [hAX, hAZ] = swing(0.78, 0.05);
  const [hBX, hBZ] = swing(0.78, -0.05);
  const rotation: [number, number, number] = [0, -angle, 0];
  return [
    // door leaf (0.88 wide, 2.02 high, hinged at its -X edge)
    { geometry: new THREE.BoxGeometry(0.88, 2.02, 0.05), color: OAK, position: [leafX, 1.01, leafZ], rotation },
    // lever handles on both faces, at the latch side
    { geometry: new THREE.BoxGeometry(0.16, 0.03, 0.04), color: BLACK, position: [hAX, 1.0, hAZ], rotation },
    { geometry: new THREE.BoxGeometry(0.16, 0.03, 0.04), color: BLACK, position: [hBX, 1.0, hBZ], rotation },
  ];
}

export const wallDoor = defineModel({
  id: "wall-door",
  batchId: "modern",
  name: "Wall with door",
  tags: ["modern", "interior", "structure", "wall", "door"],
  summary: "A wall segment with a hinged oak door, closed or swung open.",
  description:
    "A one-tile plastered wall of the Modern Living kit with a hinged oak " +
    "door leaf in the standard 0.9 x 2.05 m opening. The closed variant is " +
    "the default; the open variant swings the leaf about 80 degrees into +Z " +
    "on its hinge edge. Same 0.12 m thickness and 2.6 m height as wall-plain.",
  tiles: { w: 1, h: 1 },
  footprint: 1.4,
  variants: [
    {
      id: "closed",
      label: "Closed",
      parts(): Part[] {
        return [...shell(), ...leaf(0)];
      },
    },
    {
      id: "open",
      label: "Open",
      parts(): Part[] {
        return [...shell(), ...leaf((80 * Math.PI) / 180)];
      },
    },
  ],
  animations: [],
});
